import type { PersonaId } from './personas';

export type FeedbackDepth = 'grade-only' | 'brief' | 'detailed';

export interface WorkloadInputs {
  papersPerWeek: number;
  questionsPerPaper: number;
  /** Percentage (0-100) of papers that are math */
  mathPct: number;
  /** Percentage (0-100) of papers that are essays */
  essayPct: number;
  feedbackDepth: FeedbackDepth;
  /** Number of teachers/tutors (for center-owner and school-leader) */
  staffCount: number;
}

/** Minutes per paper/question by subject and feedback depth */
const TIME_PER_ITEM: Record<string, Record<FeedbackDepth, number>> = {
  math:         { 'grade-only': 0.5,    brief: 1.5,    detailed: 4.5 },
  essay:        { 'grade-only': 1,      brief: 2,      detailed: 10 },
  shortAnswer:  { 'grade-only': 5 / 60, brief: 15 / 60, detailed: 1 },
};

/** IntelGrader saves this fraction of grading time */
const REDUCTION_FACTOR = 0.85;

const WEEKS_PER_YEAR = 40; // academic year

export function defaultInputsForPersona(persona: PersonaId): WorkloadInputs {
  switch (persona) {
    case 'teacher':
      return { papersPerWeek: 30, questionsPerPaper: 10, mathPct: 60, essayPct: 20, feedbackDepth: 'brief', staffCount: 1 };
    case 'center-owner':
      return { papersPerWeek: 25, questionsPerPaper: 10, mathPct: 70, essayPct: 10, feedbackDepth: 'brief', staffCount: 5 };
    case 'school-leader':
      return { papersPerWeek: 30, questionsPerPaper: 10, mathPct: 50, essayPct: 30, feedbackDepth: 'brief', staffCount: 10 };
    default:
      return { papersPerWeek: 20, questionsPerPaper: 10, mathPct: 60, essayPct: 20, feedbackDepth: 'brief', staffCount: 1 };
  }
}

/**
 * Calculate total weekly grading hours for a single grader.
 * Short-answer % = 100 - mathPct - essayPct.
 */
export function calculateWeeklyHours(inputs: WorkloadInputs): number {
  const { papersPerWeek, questionsPerPaper, mathPct, essayPct, feedbackDepth, staffCount } = inputs;
  const saPct = Math.max(0, 100 - mathPct - essayPct);

  const mathPapers = papersPerWeek * (mathPct / 100);
  const essayPapers = papersPerWeek * (essayPct / 100);
  const saPapers = papersPerWeek * (saPct / 100);

  const mathMinutes = mathPapers * TIME_PER_ITEM.math[feedbackDepth];
  const essayMinutes = essayPapers * TIME_PER_ITEM.essay[feedbackDepth];
  const saMinutes = saPapers * questionsPerPaper * TIME_PER_ITEM.shortAnswer[feedbackDepth];

  const totalMinutesPerGrader = mathMinutes + essayMinutes + saMinutes;
  return (totalMinutesPerGrader * Math.max(1, staffCount)) / 60;
}

/** Hours saved per week with IntelGrader */
export function hoursSaved(weeklyHours: number): number {
  return weeklyHours * REDUCTION_FACTOR;
}

/** Hours saved per year */
export function yearlySaved(weeklyHoursSaved: number): number {
  return weeklyHoursSaved * WEEKS_PER_YEAR;
}

/** Convert hours to "full work weeks" (40-hour weeks) */
export function toWorkWeeks(yearlyHours: number): number {
  return Math.round(yearlyHours / 40);
}

export interface LifestyleCard {
  emoji: string;
  text: string;
}

export function getLifestyleCards(hoursPerWeek: number): LifestyleCard[] {
  const cards: LifestyleCard[] = [
    { emoji: '\u{1F4DA}', text: 'Read 2 books a month' },
    { emoji: '\u{26BD}', text: 'Coach your kid\u2019s soccer team' },
    { emoji: '\u{1F37D}\uFE0F', text: 'Have dinner with family every night' },
    { emoji: '\u{1F3B8}', text: 'Finally learn that instrument' },
    { emoji: '\u{1F634}', text: 'Just… sleep' },
  ];
  if (hoursPerWeek >= 10) {
    cards.unshift({ emoji: '\u{1F3D6}\uFE0F', text: 'Take a real weekend off' });
  }
  return cards;
}

/** Per-persona value messages beyond time savings */
export function getValueMessages(persona: PersonaId): string[] {
  switch (persona) {
    case 'teacher':
      return [
        'Give feedback on every paper, not just the top 5',
        'Know exactly which students need help before they fall behind',
        'Spend prep time on lesson plans, not red pen',
      ];
    case 'center-owner':
      return [
        'Show parents tangible proof of progress',
        'Handle 3x more students per tutor',
        'Eliminate parent-teacher grading disputes',
      ];
    case 'school-leader':
      return [
        'Data for board meetings without manual aggregation',
        'Reduce teacher burnout and improve retention',
        'Consistent grading standards across all teachers',
      ];
    default:
      return [];
  }
}
